// Custom leather patch hats. Targets: custom leather patch hats 1,900 (KD 0,
// $7.44 CPC; 14,800 in 2024), leather patch hats, custom leather patches.
// Kodiak Wholesale ranks with a $250 minimum. Leather engraving is already
// offered; hats and attaching the patches are quoted per project.
const {
  quoted,
  setup,
  each,
  small,
  costAnswer,
  timingAnswer,
  minimumAnswer,
  ownItemsAnswer,
  shipAnswer,
  engravingItem,
} = require('./shared')

module.exports = engravingItem({
  key: 'leatherPatchHats',
  slug: 'leather-patch-hats',
  path: '/services/laser-engraving/leather-patch-hats/',
  noun: 'hat',
  crumb: 'Leather patch hats',
  title: 'Custom Leather Patch Hats with Your Logo | PrintAWorld',
  description:
    'Custom leather patch hats and patches laser engraved with your logo in NYC: trucker hats and caps, real or faux leather, for teams and brands.',
  eyebrow: 'Leather patch hats / engraved in New York',
  heading:
    'Custom leather patch hats: your logo engraved on real or faux leather patches.',
  answer: `PrintAWorld laser-engraves custom leather patches in New York and puts them on hats: trucker hats, dad caps and beanies with your logo for a team, a brand or a business. We cut the patch to any shape and engrave the logo into real or faux leather. Engraving is a ${setup} setup plus ${each(small, 'patch')}; the hats and attaching the patches are ${quoted.toLowerCase()}.`,
  cta: 'Get a hat quote',
  heroImage: 'truckerHat',
  ideaImages: ['hatRow', 'patchCloseup', 'patchSheet'],
  images: {
    truckerHat: {
      file: 'trucker-hat',
      alt: 'Charcoal trucker hat with a tan leather patch engraved with a mountain logo',
      caption: 'An engraved patch on a trucker hat',
    },
    hatRow: {
      file: 'hat-row',
      alt: 'Row of hats in olive, black, navy and khaki with the same engraved leather patch',
      caption: 'One patch, any hat color',
    },
    patchCloseup: {
      file: 'patch-closeup',
      alt: 'Close-up of a leather patch with a crisp engraved logo, stitched to a hat',
      caption: 'The engraving up close',
    },
    patchSheet: {
      file: 'patch-sheet',
      alt: 'Sheet of laser-cut hexagon leather patches all engraved with the same logo, next to a hat',
      caption: 'A run of patches, cut and engraved',
    },
  },
  intro: {
    eyebrow: 'Patches, cut and engraved',
    title: 'Leather patch hats with your logo, in any shape.',
    paragraphs: [
      'The laser cuts the patch outline and engraves the logo in one pass, so the patch can be a circle, a hexagon or the shape of your logo. Leather engraves a shade darker, with fine detail.',
      'Send the hats you have, or tell us the style and colors and we source them. Brands that sew their own can order the patches alone.',
    ],
  },
  ideas: {
    eyebrow: 'Patch hat ideas',
    title: 'Trucker hats, caps and patches.',
  },
  uses: {
    eyebrow: 'Who orders them',
    title: 'Leather patch hats for teams, brands and crews.',
    items: [
      {
        label: 'Brands',
        title: 'Merch to sell',
        body: 'Your logo on hats for your store or pop-up, priced per piece for each quantity.',
      },
      {
        label: 'Businesses',
        title: 'Staff and crew hats',
        body: 'Hats for restaurant staff, job sites and shops, all with the same patch.',
      },
      {
        label: 'Teams and clubs',
        title: 'Team hats',
        body: 'Golf outings, rec leagues and run clubs.',
      },
      {
        label: 'Events',
        title: 'Giveaways and swag',
        body: 'Hats for every attendee, or patches engraved live at your event.',
      },
      {
        label: 'Makers',
        title: 'Patches only',
        body: 'Engraved patches for brands that sew or press their own.',
      },
    ],
  },
  materials: {
    link: 'Leather and hats',
    eyebrow: 'Leather and hats',
    title: 'Real leather, faux leather and the hats.',
    items: [
      {
        title: 'Real leather',
        body: 'Natural, tan and dark leathers engrave with a warm, burned look.',
      },
      {
        title: 'Faux leather',
        body: 'Two-tone faux leather (leatherette) engraves to a second color for strong contrast.',
      },
      {
        title: 'Any shape',
        body: 'Circles, hexagons, rectangles or your logo’s outline, cut on the laser.',
      },
      {
        title: 'The hats',
        body: 'Trucker hats, dad caps, flat bills and beanies, yours or sourced by us.',
      },
    ],
  },
  process: {
    title: 'From your logo to a box of patch hats.',
    note: 'Standard lead time is 2–3 weeks after the hats reach us.',
    steps: [
      {
        title: 'Send the logo and hats',
        body: 'Your logo, the patch shape and leather, the hat style and the count.',
      },
      {
        title: 'Approve a sample patch',
        body: 'We engrave a sample patch and send a photo.',
      },
      {
        title: 'We make the run',
        body: 'Patches are cut, engraved and heat pressed onto each hat.',
      },
      {
        title: 'Pack and ship',
        body: 'Shipped anywhere in the U.S. or picked up in NYC.',
      },
    ],
  },
  pricing: {
    title: 'Leather patch hat pricing, per piece.',
    cta: 'Price my hats',
    rows: [
      { label: 'Patch, cut and engraved', value: small },
      { label: 'The hats', value: quoted },
      { label: 'Attaching patches to hats', value: quoted },
      { label: 'Patches only', value: small },
      { label: 'Rush', value: 'Case by case' },
    ],
  },
  bulk: {
    eyebrow: 'Patch hats in bulk',
    title: 'The same patch on every hat, in any color.',
    paragraphs: [
      'For bulk orders we make one sample hat, then the run. Mix hat colors and styles in one order with the same patch.',
      'There is no minimum order; the setup is paid once per job.',
    ],
    cta: 'Plan a hat order',
  },
  faqTitle: 'Leather patch hat questions, answered.',
  faqs: [
    {
      question: 'How much do custom leather patch hats cost?',
      answer: costAnswer(
        small,
        `The hats and attaching the patches are ${quoted.toLowerCase()}.`
      ),
    },
    {
      question: 'Is there a minimum order?',
      answer: minimumAnswer('hat'),
    },
    {
      question: 'Can you put patches on hats I already have?',
      answer: ownItemsAnswer('hats'),
    },
    {
      question: 'Real or faux leather?',
      answer:
        'Real leather engraves a darker shade with a warm, natural look. Two-tone faux leather engraves to a contrasting color, which reads better from a distance.',
    },
    {
      question: 'Can you make the patches only?',
      answer:
        'Yes. We cut and engrave patches for brands that attach them themselves.',
    },
    {
      question: 'How long does it take?',
      answer: timingAnswer('Tell us if you need the hats for an event.'),
    },
    {
      question: 'Do you ship?',
      answer: shipAnswer,
    },
  ],
  quote: {
    title: 'Tell us about your hats.',
    body: 'Send the logo, the patch shape, the hat style and colors, and how many. We’ll reply with a sample plan and a written quote.',
    checklist: [
      'Your logo, vector if you have it',
      'Patch shape and leather color',
      'Hat style and colors, or your own hats',
      'How many, and your in-hand date',
    ],
  },
  moreCards: ['corporateGifts', 'tumblers', 'onsiteEngraving'],
  schema: {
    name: 'Custom Leather Patch Hats',
    alternateName: [
      'Leather patch hats with logo',
      'Custom leather patches',
      'Engraved leather patch caps',
    ],
    serviceType: 'Laser-engraved leather patches and hats',
  },
})
